// src/components/dashboard/SupportContactModal.jsx
//
// The "personal connection outlet" — a real human an org points its members to
// when they tap "Talk to Someone" / "Get more help", instead of routing them to
// video content. Contents are configured per-organization in the super-admin
// Organization editor (organizations/{id}.supportContact) and arrive here via
// orgData from OrganizationContext.
//
// Also carries the org's EAP/EFAP program block (organizations/{id}.eapProgram,
// see docs/plans/EAP-TIEIN-PLAN.md) — the route into the plan's actual service providers.
// Either section renders alone; together the personal contact leads and the
// EAP follows as "your benefits also include...".

import React, { useState } from 'react';
import { HeartHandshake, Mail, Phone, CalendarClock, X, ExternalLink, LifeBuoy, ShieldCheck, KeyRound, MessageSquare, Smartphone, Languages, Users, ChevronDown } from 'lucide-react';
import CrisisResources from './CrisisResources';
import SupportPathways from './SupportPathways';
import { useOrganization } from '../../context/OrganizationContext';
import { resolveSupportPathways, memberPathwaysFor } from '../../data/supportPathways';
import { resourcesFor, allResourcesFor } from '../../data/crisisResources';
import SaveContactButton from './SaveContactButton';
import { normalizeServices, withAttribution, availabilityLabel } from '../../utils/eapProgram';

// Who actually provides the program. A member recognises their provider's mark
// (TELUS Health, Homewood, a carrier's EFAP) far faster than they read our copy,
// which is the difference between "some benefit" and "the thing on my card".
// Falls back to the plain "Provided by …" line when there's no logo or the image
// fails to load — the section must never render a broken tile.
const ProviderLockup = ({ eap }) => {
    const [failed, setFailed] = useState(false);

    if (!eap.logoUrl || failed) {
        const parts = [eap.providerName && `Provided by ${eap.providerName}`, eap.sessionsNote].filter(Boolean);
        if (parts.length === 0) return null;
        return <p className="text-xs text-slate-500 font-medium mb-3">{parts.join(' · ')}</p>;
    }

    return (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-4 py-4 text-center shadow-sm">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5">Provided by</p>
            <img
                src={eap.logoUrl}
                alt={eap.providerName || 'Assistance program provider'}
                onError={() => setFailed(true)}
                className="mx-auto max-h-14 max-w-[220px] object-contain"
            />
            {eap.sessionsNote && <p className="text-xs text-slate-500 font-medium mt-3">{eap.sessionsNote}</p>}
        </div>
    );
};

// What the phone's Contacts app gets. Both the provider name and the program
// label end up searchable (FN carries both, ORG the provider), because a member
// looking for this later types whichever one they remember.
const eapContactFields = (eap) => {
    const label = eap.programLabel || 'Assistance Program';
    const services = normalizeServices(eap.services);
    const note = [
        eap.accessCode && `Access code: ${eap.accessCode}`,
        eap.accessNote,
        eap.availability && `Available: ${eap.availability}`,
        eap.crisisPhone && `Crisis line: ${eap.crisisPhone}`,
        eap.ttyPhone && `TTY: ${eap.ttyPhone}`,
        eap.internationalPhone && `From outside Canada/US: ${eap.internationalPhone}`,
        eap.sessionsNote,
        services.length > 0 ? `Includes: ${services.map((s) => s.label).join(', ')}` : null,
        'Free & confidential.'
    ].filter(Boolean).join('\n');

    return {
        fullName: eap.providerName ? `${eap.providerName} — ${label}` : label,
        org: eap.providerName || '',
        title: label,
        phone: eap.phone || eap.crisisPhone || '',
        email: eap.email || '',
        urls: [eap.url, eap.appUrl].filter(Boolean),
        note
    };
};

// The access/group code is the single most common reason an intake call fails —
// the member gets through and can't identify their employer. It gets its own
// tappable-to-copy row rather than being buried in the access note.
const AccessCode = ({ code }) => {
    const [copied, setCopied] = useState(false);
    if (!code) return null;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            // Clipboard blocked (insecure context, permissions) — the code is
            // on screen regardless, so there is nothing to recover from.
        }
    };

    return (
        <button
            type="button"
            onClick={copy}
            className="w-full flex items-center gap-3 p-3 mb-4 rounded-xl bg-amber-50 border border-amber-200 text-left hover:bg-amber-100 transition-colors"
        >
            <KeyRound size={16} className="text-amber-600 shrink-0" />
            <span className="min-w-0">
                <span className="block text-[10px] font-black text-amber-700 uppercase tracking-widest">Give them this code</span>
                <span className="block text-sm font-black text-slate-900 truncate">{code}</span>
            </span>
            <span className="ml-auto shrink-0 text-[10px] font-black uppercase tracking-wider text-amber-700">
                {copied ? 'Copied' : 'Copy'}
            </span>
        </button>
    );
};

// One tappable row per channel, so phone / crisis line / text / chat / TTY all
// render identically instead of each growing its own markup.
const ChannelRow = ({ icon: Icon, href, label, meta, external }) => (
    <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className="flex items-center gap-3 w-full p-3 rounded-xl bg-brand-light text-brand-dark hover:bg-slate-100 border border-slate-200 transition-colors font-bold text-sm"
    >
        <Icon size={18} className="text-brand-primary shrink-0" />
        <span className="truncate">{label}</span>
        {meta && (
            <span className="ml-auto text-[10px] font-black text-slate-400 uppercase tracking-wider shrink-0">{meta}</span>
        )}
    </a>
);

const EapBlock = ({ eap, hasContactAbove }) => {
    const services = normalizeServices(eap.services);
    const tel = (n) => `tel:${String(n).replace(/[^+\d]/g, '')}`;
    // Outbound links carry whatever attribution the provider asked for, so usage
    // on their side traces back here.
    const link = (url) => withAttribution(url, eap.linkParams);
    const availability = availabilityLabel(eap);

    return (
        <div className={hasContactAbove ? 'mt-6 pt-5 border-t border-slate-100' : ''}>
            <div className="flex items-center gap-2 mb-1">
                <LifeBuoy size={16} className="text-brand-primary shrink-0" />
                <h4 className="font-black text-slate-900 text-sm">
                    {hasContactAbove ? 'Your benefits also include' : (eap.programLabel || 'Your assistance program')}
                </h4>
            </div>
            {hasContactAbove && (
                <p className="text-xs font-bold text-brand-primary mb-1">{eap.programLabel || 'Assistance program'}</p>
            )}
            <ProviderLockup eap={eap} />

            {eap.accessNote && (
                <p className="text-sm text-slate-600 leading-relaxed font-medium mb-4">{eap.accessNote}</p>
            )}

            <AccessCode code={eap.accessCode} />

            {/* SERVICES — each one its own intake where the provider has a deep
                link. A single portal URL makes the member hunt for the right door. */}
            {services.length > 0 && (
                <div className="mb-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">What it covers</p>
                    <div className="space-y-1.5">
                        {services.map((s, i) => (
                            s.url ? (
                                <a
                                    key={i}
                                    href={link(s.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-2 w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:border-brand-primary/40 hover:bg-white transition-colors"
                                >
                                    <span className="min-w-0">
                                        <span className="block text-sm font-bold text-brand-primary truncate">{s.label}</span>
                                        {s.hint && <span className="block text-xs text-slate-500 font-medium">{s.hint}</span>}
                                    </span>
                                    <ExternalLink size={13} className="ml-auto mt-1 shrink-0 text-slate-400" />
                                </a>
                            ) : (
                                <div key={i} className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                                    <span className="block text-sm font-bold text-slate-700">{s.label}</span>
                                    {s.hint && <span className="block text-xs text-slate-500 font-medium">{s.hint}</span>}
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-2.5">
                {eap.phone && (
                    <ChannelRow icon={Phone} href={tel(eap.phone)} label={eap.phone} meta={availability} />
                )}
                {eap.crisisPhone && (
                    <ChannelRow icon={LifeBuoy} href={tel(eap.crisisPhone)} label={eap.crisisPhone} meta="Crisis" />
                )}
                {eap.smsNumber && (
                    <ChannelRow icon={MessageSquare} href={`sms:${String(eap.smsNumber).replace(/[^+\d]/g, '')}`} label={`Text ${eap.smsNumber}`} />
                )}
                {eap.chatUrl && (
                    <ChannelRow icon={MessageSquare} href={link(eap.chatUrl)} label="Live chat" external />
                )}
                {eap.email && (
                    <ChannelRow icon={Mail} href={`mailto:${eap.email}`} label={eap.email} />
                )}
                {eap.ttyPhone && (
                    <ChannelRow icon={Phone} href={tel(eap.ttyPhone)} label={eap.ttyPhone} meta="TTY" />
                )}
                {eap.internationalPhone && (
                    <ChannelRow icon={Phone} href={tel(eap.internationalPhone)} label={eap.internationalPhone} meta="Abroad" />
                )}
                {eap.appUrl && (
                    <ChannelRow icon={Smartphone} href={link(eap.appUrl)} label="Get the provider's app" external />
                )}
                {eap.url && (
                    <a
                        href={link(eap.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full p-3.5 rounded-xl bg-brand-primary text-white hover:bg-brand-dark transition-colors font-black text-sm shadow-lg shadow-brand-primary/30"
                    >
                        <ExternalLink size={16} className="shrink-0" />
                        Open member portal
                    </a>
                )}
                {/* Sets the expectation before the tap — an unexpected login wall is
                    where people give up. */}
                {eap.url && eap.requiresLogin && (
                    <p className="text-[11px] text-slate-400 font-medium text-center leading-relaxed">
                        You'll be asked to sign in{eap.accessCode ? ' — use the code above' : ''}.
                    </p>
                )}

                {/* Into the phone's own Contacts app — where someone actually looks
                    for a number at the moment they need one. */}
                <SaveContactButton fields={eapContactFields(eap)} label="Save this program to my contacts" />
            </div>

            {(eap.languageNote || eap.eligibilityNote || eap.sessionsNote) && (
                <div className="mt-4 space-y-1">
                    {eap.eligibilityNote && (
                        <p className="text-[11px] text-slate-500 font-medium leading-relaxed flex items-start gap-1.5">
                            <Users size={12} className="shrink-0 mt-0.5" />{eap.eligibilityNote}
                        </p>
                    )}
                    {eap.languageNote && (
                        <p className="text-[11px] text-slate-500 font-medium leading-relaxed flex items-start gap-1.5">
                            <Languages size={12} className="shrink-0 mt-0.5" />{eap.languageNote}
                        </p>
                    )}
                </div>
            )}

            <p className="text-[11px] text-slate-400 font-medium text-center mt-4 leading-relaxed flex items-center justify-center gap-1.5">
                <ShieldCheck size={13} className="shrink-0" />
                Free &amp; confidential — your organization never sees who uses it.
            </p>
        </div>
    );
};

// topicId / memberType pick the Canadian lines shown at the bottom (the floor
// beneath the org's own contact / EAP — present even when neither is configured).
// showCrisisFloor: only a surface that offers its OWN urgent route (the dashboard
// hero's "I need help now") may switch the floor off — every other caller keeps it,
// including the safety-flagged paths that open this modal precisely because
// someone may be in trouble.
const SupportContactModal = ({ contact, eap, onClose, topicId = null, memberType = null, ageTier = null, focus = null, showCrisisFloor = true }) => {
    const hasContact = !!(contact && (contact.email || contact.phone || contact.bookingUrl));
    const hasAnyOrgOutlet = hasContact || !!eap;
    // Opened from the urgent outlet: the crisis lines lead, and everything else
    // follows. Someone who tapped "I need help now" should not have to scroll
    // past a benefits programme to find a phone number.
    const crisisFirst = focus === 'crisis';

    // Help & Support Pathways (data/supportPathways.js): a person they know and
    // the words to start, when the org has opted in.
    const { orgData } = useOrganization();
    const pathwaySections = memberPathwaysFor(resolveSupportPathways(orgData), { memberType, ageTier });
    const hasPathways = pathwaySections.length > 0;
    // The broader list sits behind the contextual few: lines in context first,
    // the full set there for anyone who wants to explore it.
    const [showAllLines, setShowAllLines] = useState(false);
    const shownIds = new Set(resourcesFor(topicId, memberType, ageTier).map(l => l.id));
    const moreLines = allResourcesFor(memberType, ageTier).filter(l => !shownIds.has(l.id));

    const { name, role, email, phone, bookingUrl, message, photoUrl } = contact || {};

    return (
        <div
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-md rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header band — brand colored. Pinned: only the body below scrolls. */}
                <div className={`relative shrink-0 bg-gradient-to-br from-brand-primary to-brand-dark p-6 text-white ${hasContact ? 'pb-14' : ''}`}>
                    <div className="flex items-center gap-2 text-blue-100 text-[11px] font-black uppercase tracking-widest">
                        <HeartHandshake size={15} /> {crisisFirst ? 'Help, right now' : hasContact ? 'A real person, here for you' : hasAnyOrgOutlet ? 'Support, here for you' : 'You are not on your own'}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Avatar overlapping the band — needs relative+z so it paints
                    ABOVE the position:relative header band, not behind it. */}
                {hasContact && (
                    <div className="px-6 -mt-10 relative z-10 shrink-0">
                        <div className="w-20 h-20 rounded-2xl bg-white shadow-lg border border-slate-100 flex items-center justify-center overflow-hidden">
                            {photoUrl ? (
                                <img src={photoUrl} alt={name || 'Support contact'} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-brand-light text-brand-primary flex items-center justify-center">
                                    <HeartHandshake size={34} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Body — the only scrolling region */}
                <div className={`overflow-y-auto custom-scrollbar p-6 ${hasContact ? 'pt-3' : ''}`}>
                    {crisisFirst && (
                        <div className={hasAnyOrgOutlet ? 'mb-6 pb-5 border-b border-slate-100' : ''}>
                            <CrisisResources topicId={topicId} memberType={memberType} ageTier={ageTier} />
                            {hasAnyOrgOutlet && (
                                <p className="text-[11px] text-slate-400 font-medium mt-3 leading-relaxed">
                                    Not an emergency? Your other support options are below.
                                </p>
                            )}
                        </div>
                    )}

                    {hasContact && (
                        <>
                            {name && <h3 className="text-xl font-black text-slate-900">{name}</h3>}
                            {role && <p className="text-sm font-bold text-brand-primary mb-3">{role}</p>}

                            {message && (
                                <p className="text-sm text-slate-600 leading-relaxed font-medium mb-5 whitespace-pre-line">
                                    {message}
                                </p>
                            )}

                            <div className="space-y-2.5">
                                {email && (
                                    <a
                                        href={`mailto:${email}`}
                                        className="flex items-center gap-3 w-full p-3 rounded-xl bg-brand-light text-brand-dark hover:bg-slate-100 border border-slate-200 transition-colors font-bold text-sm"
                                    >
                                        <Mail size={18} className="text-brand-primary shrink-0" />
                                        <span className="truncate">{email}</span>
                                    </a>
                                )}
                                {phone && (
                                    <a
                                        href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                                        className="flex items-center gap-3 w-full p-3 rounded-xl bg-brand-light text-brand-dark hover:bg-slate-100 border border-slate-200 transition-colors font-bold text-sm"
                                    >
                                        <Phone size={18} className="text-brand-primary shrink-0" />
                                        <span className="truncate">{phone}</span>
                                    </a>
                                )}
                                {bookingUrl && (
                                    <a
                                        href={bookingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full p-3.5 rounded-xl bg-brand-primary text-white hover:bg-brand-dark transition-colors font-black text-sm shadow-lg shadow-brand-primary/30"
                                    >
                                        <CalendarClock size={18} className="shrink-0" />
                                        Book a time
                                        <ExternalLink size={14} className="opacity-80" />
                                    </a>
                                )}
                                {(email || phone || bookingUrl) && (
                                    <SaveContactButton
                                        fields={{
                                            fullName: name || 'Support contact',
                                            title: role || '',
                                            email: email || '',
                                            phone: phone || '',
                                            urls: [bookingUrl].filter(Boolean),
                                            note: message || ''
                                        }}
                                        label={name ? `Save ${name} to my contacts` : 'Save to my contacts'}
                                    />
                                )}
                            </div>

                            <p className="text-[11px] text-slate-400 font-medium text-center mt-5 leading-relaxed">
                                This is a private, direct line — your scores and check-ins are never shared as part of reaching out.
                            </p>
                        </>
                    )}

                    {eap && <EapBlock eap={eap} hasContactAbove={hasContact} />}

                    {hasPathways && (
                        <div className={hasAnyOrgOutlet || crisisFirst ? 'mt-6 pt-5 border-t border-slate-100' : ''}>
                            <SupportPathways sections={pathwaySections} />
                        </div>
                    )}

                    {/* Canada-correct floor. Skipped when the crisis lines already led,
                        or when the calling surface carries its own urgent outlet — but
                        never when there is nothing else in the modal to show. */}
                    {!crisisFirst && (showCrisisFloor || !hasAnyOrgOutlet) && (
                        <div className={hasAnyOrgOutlet || hasPathways ? 'mt-6 pt-5 border-t border-slate-100' : ''}>
                            {!hasAnyOrgOutlet && !hasPathways && (
                                <p className="text-sm text-slate-600 leading-relaxed font-medium mb-4">Your organization hasn't set up a personal contact yet. These lines are free, confidential and open now.</p>
                            )}
                            <CrisisResources topicId={topicId} memberType={memberType} ageTier={ageTier} />
                        </div>
                    )}

                    {moreLines.length > 0 && (
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => setShowAllLines(s => !s)}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 text-left text-sm font-black text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                                <span>Other kinds of support</span>
                                <ChevronDown size={16} className={`shrink-0 transition-transform ${showAllLines ? 'rotate-180' : ''}`} />
                            </button>
                            {showAllLines && (
                                <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200">
                                    <CrisisResources lines={moreLines} memberType={memberType} ageTier={ageTier} heading="Bullying, safety at home, substance use and more" compact showEmergencyNote={false} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportContactModal;
